/* Dr.Primal — the cart reconciliation guard.

   WHAT IT GUARDS, stated accurately, because the original answer is no longer true. It
   was written to catch a free-gift Buy X Get Y that failed to fire. There are no gifts
   in the offer any more, and its second rationale — a subscription discount that fails
   to apply — is gone too, because the saving now lives in the variant price and there is
   no subscription discount that CAN fail.

   What it still catches, and why it earns its place, is the class of failure underneath
   both of those: a silent divergence between the figure the page advertised and the
   figure the cart charges. A page rendered against a price that has since been changed
   in the admin. A market or currency mismatch between what Liquid rendered and what the
   cart returns. Any automatic discount nobody expected. Each of those reaches checkout
   with nothing else on the storefront able to see it.

   `dp-buy-box.js` compares what the cart actually cost against the BILLED total the
   selected offer advertised — 117.00 on the 90-Day offer, not the 39.00 it shows per
   month — and hands the result to this module. This module is what the CHECKOUT
   controls read, because the drawer is reachable from the header on every page without
   going through the buy box, and that is the one path a silent overcharge could still
   take to checkout.

   It lives in its own file, loaded beside the cart drawer on every page, for that
   reason: the flag has to outlive the product page that raised it.

   - The flag is kept in `localStorage`, STAMPED WITH THE CART TOKEN, because the cart
     cookie lives fourteen days and is shared by every tab. A `sessionStorage` flag died
     with the tab while the wrongly priced basket lived on, so a customer who came back
     tomorrow — or opened a second tab from a bookmark — got a live Checkout button on
     the same wrong cart. The token is what stops the flag outliving the basket that
     earned it: a cart that was emptied and rebuilt has a different token, and the flag
     is dropped the first time that is seen.
   - It is re-evaluated against `/cart.js` on every cart update and every drawer open,
     and clears itself the moment the offending lines are gone or have become free. A
     customer who removes the wrongly priced line gets Checkout back without a reload.
   - There is deliberately NO public `clear()`. Only a fresh look at `/cart.js` may lift
     the block; nothing gets to assert the basket is fine without having checked.
   - A flagged line is identified by variant id AND selling plan, because Shopify keeps
     the one-time and the subscription line for the same variant apart. An entry with no
     plan recorded matches the variant whatever plan it carries — that is the deliberate
     broad match for the case where the verification itself could not be made.
   - It never formats money and never decides a price. It compares integers that Liquid
     stamped against integers the Cart API returned. The message it shows is written by
     Liquid too, through `| t`; this file contains no user-facing English. */
(function () {
  'use strict';

  var KEY = 'dp:cart:guard';
  var CHECKOUT = '[name="checkout"]';
  var MESSAGE = '[data-dp-cart-guard]';
  var FALLBACK = 'data-dp-guard-fallback';

  // The cart cookie lives fourteen days. A flag older than that describes a basket the
  // customer can no longer have, and the token check below would drop it anyway — this
  // is the belt to that pair of braces, for the case where `/cart.js` cannot be reached.
  var MAX_AGE = 15 * 24 * 60 * 60 * 1000;

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  // Private mode and blocked storage both throw on access. Without storage the guard
  // still works for the life of the page; it just cannot survive a navigation.
  var memory = null;

  /* A flagged line is `{ id, plan }`. `plan` is a string — '' for a one-time line — or
     null, which means "match this variant whatever plan it carries". */
  function entry(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') {
      if (value.id === null || value.id === undefined) return null;
      return {
        id: String(value.id),
        plan: value.plan === null || value.plan === undefined ? null : String(value.plan),
      };
    }
    return { id: String(value), plan: null };
  }

  function entries(list) {
    var out = [];
    (list || []).forEach(function (value) {
      var line = entry(value);
      if (line) out.push(line);
    });
    return out;
  }

  /* `blocked` is stored in its own right. The decision to block must never depend on a
     copy string being present — an empty message used to read as "not blocked", which is
     the failure mode this guard exists to prevent, produced by the guard itself. */
  function read() {
    var raw;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch (error) {
      return memory;
    }
    if (!raw) return memory;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return memory;
    }
    if (!parsed || parsed.blocked !== true) return null;
    if (parsed.at && Date.now() - parsed.at > MAX_AGE) {
      write(null);
      return null;
    }
    parsed.lines = entries(parsed.lines);
    return parsed;
  }

  function write(value) {
    memory = value;
    try {
      if (value) window.localStorage.setItem(KEY, JSON.stringify(value));
      else window.localStorage.removeItem(KEY);
    } catch (error) {
      /* memory copy above is the fallback */
    }
  }

  function planOf(line) {
    return line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
      ? String(line.selling_plan_allocation.selling_plan.id)
      : '';
  }

  function matches(line, flagged) {
    if (String(line.variant_id) !== String(flagged.id)) return false;
    if (flagged.plan === null) return true;
    return planOf(line) === flagged.plan;
  }

  /* Every disabled Checkout control used to point at the drawer's message id, including
     the one on `/cart` — whose description therefore resolved into a closed, `inert`
     drawer. Each button is described by the message that shares its region, or by
     nothing at all. */
  function messageIdFor(button) {
    var node = button.parentNode;
    while (node && node !== document.body && node.querySelector) {
      var found = node.querySelector(MESSAGE);
      if (found && found.id) return found.id;
      node = node.parentNode;
    }
    // Deliberately no document-wide fallback: reaching past the button's own region is
    // how `/cart` ended up described by the drawer.
    return '';
  }

  function apply() {
    var flag = read();
    var blocked = !!flag;

    Array.prototype.forEach.call(document.querySelectorAll(CHECKOUT), function (button) {
      button.disabled = blocked;
      // `disabled` alone renders at full opacity with a pointer cursor: the dimmed,
      // not-allowed treatment lives on the class. A control that is inert but looks
      // untouched is exactly the second signal this theme requires an error state to
      // carry, missing.
      button.classList.toggle('dp-button--disabled', blocked);

      var id = blocked ? messageIdFor(button) : '';
      if (id) button.setAttribute('aria-describedby', id);
      else button.removeAttribute('aria-describedby');
    });

    Array.prototype.forEach.call(document.querySelectorAll(MESSAGE), function (node) {
      // Liquid stamps the fallback sentence through `| t`, so a flag that somehow lost
      // its message still explains itself rather than blocking in silence.
      node.textContent = blocked ? flag.message || node.getAttribute(FALLBACK) || '' : '';
      node.hidden = !blocked;
    });
  }

  /* Re-decide from the server, not from what the buy box remembered. A line the
     customer removed, or one a corrected discount has since zeroed, clears the flag.

     Lines are matched by VARIANT ID and SELLING PLAN, not by line key. A line key is not
     stable: a discount grouping is part of the hash, so a change elsewhere in the basket
     rewrites a key this flag was holding. Keying on it cleared this flag while the
     wrongly priced line was still in the basket — found in QA on 2026-08-27, and exactly
     the silent failure the guard exists to prevent.

     An offer line is always priced above zero, so once a mismatch has flagged one the
     block holds until that line is removed. That is the intended behaviour: the customer
     was charged something other than what the page advertised. */
  function evaluate() {
    var flag = read();
    if (!flag) {
      apply();
      return Promise.resolve(null);
    }

    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart.js responded ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        // A different cart entirely: the basket that earned the flag is gone.
        if (flag.token && cart.token && String(cart.token) !== String(flag.token)) {
          write(null);
          apply();
          return cart;
        }
        if (!flag.token && cart.token) {
          flag.token = String(cart.token);
          write(flag);
        }

        var stillWrong = flag.lines.some(function (flagged) {
          for (var i = 0; i < cart.items.length; i += 1) {
            if (matches(cart.items[i], flagged) && cart.items[i].final_line_price > 0) {
              return true;
            }
          }
          return false;
        });
        if (!stillWrong) write(null);
        apply();
        return cart;
      })
      .catch(function (error) {
        // Leaving the flag up is the safe failure: checkout stays blocked rather than
        // opening because a status check could not be made.
        console.error('[dp-cart-guard]', error);
        apply();
        return null;
      });
  }

  window.DPCartGuard = {
    /* `lines` are `{ id, plan }` pairs, or bare variant ids where the plan is unknown.
       `token` is the cart the flag belongs to, when the caller has just read it. */
    raise: function (message, lines, token) {
      write({
        blocked: true,
        message: message || '',
        lines: entries(lines),
        token: token ? String(token) : null,
        at: Date.now(),
      });
      apply();
    },
    isBlocked: function () {
      return !!read();
    },
    evaluate: evaluate,
    apply: apply,
  };

  function init() {
    apply();
    if (read()) evaluate();

    // The drawer replaces its footer — and with it the Checkout button — on every
    // update, so the guard is re-applied after the drawer has finished rendering.
    document.addEventListener('dp:cart:updated', function () {
      window.setTimeout(evaluate, 0);
    });

    document.addEventListener('dp:cart:open', function () {
      window.setTimeout(evaluate, 0);
    });

    document.addEventListener('dp:cart:guard', function () {
      window.setTimeout(apply, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
